import React, { useEffect, useState } from "react";
import { NMAxiosInst } from "@neumetric/blueprint-lib/store";
import { NMStatsTable, BarChart, Col } from "@neumetric/blueprint-lib/components";
import { ExportSection } from "../ExportSection";

const MODULE_KEY = "Investigator_Department_vs_Incident_Status";

export const InvestigatorDepartmentStats = (props) => {
  const { departments, user, Columns, dateRange, dateRangeList } = props;
  const [responseData, setResponseData] = useState({});
  const [tableData, setTableData] = useState([]);
  const dateRangeEnums = dateRangeList || [];

  const effectiveRangeId = typeof dateRange === "string" ? dateRange : dateRange?.id;

  const getDataFromServer = async (dateRange) => {
    let payload = {
      date_range_type: "last_365_days",
    };
    if (dateRange && dateRange.id) {
      payload.date_range_type = dateRange.id;
    }
    let res = await NMAxiosInst.post("/incidents/department_dashboard", payload);
    setResponseData(res);
  };

  const chartDatasets = React.useMemo(() => {
    const brand = user.org.brandname;
    const n = (v) => (v === "-" || v == null ? 0 : Number(v));
    const SERIES_ORDER = ["OTHER", "OPEN", "STARTED", "CLOSED"];

    // Map stack label -> blueprint color key
    const COLOR_KEY = {
      OTHER: "INVALID",
      OPEN: "PENDING",
      STARTED: "STARTED",
      CLOSED: "COMPLETE", // "Closed" is stored as COMPLETE in your rows
    };

    // table rows excluding the brand row
    const rows = (tableData || []).filter((r) => r && r.label && r.label !== brand);

    const byDept = new Map(rows.map((r) => [r.label, r]));

    // ✅ Always use all declared departments
    const declaredDepts = (departments || [])
      .map((d) => String(d?.id ?? d?.name ?? d))
      .filter(Boolean)
      .filter((d) => d !== brand)
      .sort((a, b) => a.localeCompare(b));

    // Build dataset for each declared dept, only including those with non-zero values
    return Object.fromEntries(
      declaredDepts
        .map((dept) => {
          const r = byDept.get(dept) || {};
          const values = {
            CLOSED: n(r.COMPLETE),
            STARTED: n(r.STARTED),
            OPEN: n(r.OPEN),
            OTHER: n(r.OTHER),
          };
          return {
            dept,
            values,
            dataset: SERIES_ORDER.map((k) => ({
              key: COLOR_KEY[k],
              value: values[k] || 0,
            })),
          };
        })
        .filter(({ values }) => {
          // Only include departments with at least one non-zero value
          return Object.values(values).some((value) => value !== 0);
        })
        .map(({ dept, dataset }) => [dept, dataset])
    );
  }, [tableData, user.org.brandname, departments]);

  const dateRangeLabel =
    dateRange?.label ??
    dateRangeEnums.find((o) => o.id === (typeof dateRange === "string" ? dateRange : dateRange?.id))?.label ??
    "";

  useEffect(() => {
    const { tableData } = getDataFromResponse(responseData, user, departments);
    setTableData(tableData);
  }, [responseData]);

  useEffect(() => {
    if (!effectiveRangeId) return;
    getDataFromServer({ id: effectiveRangeId });
  }, [effectiveRangeId]);
  return (
    <div style={{ padding: "20px 40px 20px 40px" }}>
      <div
        style={{
          display: "flex",
          width: "100%",
          gap: 20,
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        <Col className="w-100" />
      </div>

      <div
        style={{
          display: "flex",
          width: "100%",
          gap: "20px",
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        {/* Table */}
        <ExportSection
          moduleKey={MODULE_KEY}
          index={1}
          dateRangeLabel={dateRange?.label}
          exportType="table"
          exportData={{
            title: "Incident - Department-wise",
            columns: Columns,
            rows: tableData,
          }}
          style={{
            flex: "1 1 48%",
            minWidth: 400,
            maxWidth: "50%",
            paddingTop: "0",
          }}
        >
          <NMStatsTable
            id="Investigator_Department_vs_Incident_Status"
            title="Incident"
            subTitle="Department-wise"
            columns={Columns}
            dataSource={tableData}
            dateRange={dateRangeLabel}
            showDateInHeader={true}
            getCellTypeRepresentation={(cellInfo) => {
              const dotColumns = ["STARTED", "COMPLETE", "OPEN", "OTHER"];
              if (cellInfo.rowId === -1 && dotColumns.includes(cellInfo.columnId)) {
                return "DOT";
              }
              return null;
            }}
          />
        </ExportSection>

        {/* Bar Chart */}
        <ExportSection
          moduleKey={MODULE_KEY}
          index={2}
          exportType="chart"
          style={{
            flex: "1 1 48%",
            minWidth: 400,
            maxWidth: "50%",
            paddingTop: "0",
          }}
        >
          <div className="chart-export-pad" style={{ width: "100%", overflow: "visible", paddingTop: "10px" }}>
            <div style={{ width: "100%", height: 450 }}>
              <BarChart
                id="Investigator_Department_vs_Incident_Status"
                showDateInHeader={true}
                title="Incident"
                subtitle="Department-wise"
                label={{ xAxis: "Incidents", yAxis: "Department" }}
                datasets={chartDatasets}
                stackType="normal"
                dateRange={dateRangeLabel}
                classes={{ root: "h-575-px w-100" }}
              />
            </div>
          </div>
        </ExportSection>
      </div>
    </div>
  );
};
// ---------------- TABLE ----------------
const getDataFromResponse = (res, user, departments = []) => {
  if (!res || !res.department_status || Object.keys(res.department_status).length === 0) {
    // ✅ If backend sends nothing at all, return []
    return { tableData: [] };
  }

  const departmentStatus = res.department_status;
  const brandname = user.org.brandname;

  const STATUS_CATEGORY_MAP = {
    complete: "COMPLETE",
    closed: "COMPLETE",
    failed: "COMPLETE",

    started: "STARTED",
    inprogress: "STARTED",
    review: "STARTED",

    pending: "OPEN",
    open: "OPEN",
  };

  let tableData = [];
  let totalRow = {
    label: brandname,
    max_aging: 0,
    avg_aging: 0,
    total: 0,
    COMPLETE: 0,
    STARTED: 0,
    OPEN: 0,
    OTHER: 0,
    highlight: true,
  };

  let avgAgingSum = 0;
  let countAging = 0;

  // ✅ Always iterate over declared departments
  (departments || []).forEach((deptObj) => {
    const dept = String(deptObj?.id ?? deptObj?.name ?? deptObj);

    const entry = departmentStatus[dept] || { statuses: {} };
    const statuses = entry.statuses || {};

    const row = {
      label: dept,
      max_aging: entry.max_aging || 0,
      avg_aging: entry.avg_aging || 0,
      total: statuses.total || 0,
      COMPLETE: 0,
      STARTED: 0,
      OPEN: 0,
      OTHER: 0,
    };

    for (const statusKey in statuses) {
      if (statusKey === "total") continue;
      const category = STATUS_CATEGORY_MAP[statusKey.toLowerCase()] || "OTHER";
      row[category] += statuses[statusKey];
    }

    totalRow.total += row.total;
    totalRow.COMPLETE += row.COMPLETE;
    totalRow.STARTED += row.STARTED;
    totalRow.OPEN += row.OPEN;
    totalRow.OTHER += row.OTHER;
    totalRow.max_aging = Math.max(totalRow.max_aging, row.max_aging || 0);

    avgAgingSum += row.avg_aging || 0;
    if (row.avg_aging) countAging++;

    // Keep days formatting
    row.max_aging = row.max_aging > 0 ? `${row.max_aging} d` : row.max_aging;
    row.avg_aging = row.avg_aging > 0 ? `${row.avg_aging} d` : row.avg_aging;

    // Only push to tableData if row has non-zero values
    const numericFields = ["COMPLETE", "STARTED", "OPEN", "OTHER", "total"];
    const hasNonZeroValues = numericFields.some((field) => row[field] !== 0);

    if (hasNonZeroValues) {
      tableData.push(row);
    }
  });

  totalRow.avg_aging = countAging ? Math.round(avgAgingSum / countAging) : 0;
  totalRow.max_aging = totalRow.max_aging > 0 ? `${totalRow.max_aging} d` : totalRow.max_aging;
  totalRow.avg_aging = totalRow.avg_aging > 0 ? `${totalRow.avg_aging} d` : totalRow.avg_aging;

  // ✅ Final dataset: brand row + all departments
  let finalRows = [totalRow, ...tableData];

  // Convert all `0 → "-"` for display
  finalRows = convertZeroToHyphen(finalRows);

  return { tableData: finalRows };
};

function convertZeroToHyphen(rows) {
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (row[key] === 0 || row[key] === "0") {
        row[key] = "-";
      }
    });
  });
  return rows;
}
